import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

type UseTimeoutResult = {
    /**
     * The number of seconds that is left until timeout occurs
     */
    timeLeft: number;

    /**
     * If the timeout timer is started
     */
    isStarted: boolean;

    /**
     * A function that will stop and reset the timer
     */
    stopAndReset: () => void;

    /**
     * A function that will start the timer
     */
    start: () => void;

    /**
     * A function that will reset the timer. If it was running it will be kept running.
     */
    reset: () => void;
};

/**
 * A timeout hook for that will call the given method when a certain time has passed.
 * It also contains functions for resetting and starting the timeout.
 *
 * @param time the to wait before the onTimeout function will be called.
 * @param defaultStart if the timeout should start by default
 * @param onTimeout a function that will be called when the hook times out.
 * @returns
 */
const useTimeout = (time = 10, defaultStart = false, onTimeout: () => void = () => {}): UseTimeoutResult => {
    const [timeLeft, setTimeLeft] = useState(time);
    const [isStarted, setStarted] = useState(defaultStart);
    const timeoutRef: { current: NodeJS.Timeout | null } = useRef(null);
    const onTimeoutRef = useRef(onTimeout);
    useLayoutEffect(() => {
        onTimeoutRef.current = onTimeout;
    }, [onTimeout]);

    const stopAndReset = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        setStarted(false);
        setTimeLeft(time);
    }, [time]);

    const start = useCallback(() => {
        setTimeLeft(time);
        setStarted(true);
    }, [time]);

    const reset = useCallback(() => {
        setTimeLeft(time);
    }, [time]);

    useEffect(() => {
        if (timeLeft > 0 && isStarted) {
            timeoutRef.current = setTimeout(() => setTimeLeft((currentTime) => currentTime - 1), 1000);
        } else if (isStarted) {
            // timeLeft reached 0: timer has expired naturally
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            onTimeoutRef.current();
            setTimeLeft(0);
            setStarted(false);
        } else {
            // Not started: just clear any lingering timeout, no state changes
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        }
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [timeLeft, isStarted]);

    return { timeLeft, isStarted: isStarted, stopAndReset, reset, start };
};

export default useTimeout;
